import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { SafeImage } from '../../components/SafeImage';
import CustomerHeader from '../../components/CustomerHeader';
import CustomerFooter from '../../components/CustomerFooter';
import { LoadingBlock, EmptyState } from '../../components/States';
import { useCustomerAuth } from '../../lib/customerAuth';
import { useCart } from '../../lib/cart';
import { resolveProductPrice, resolveProductUnitPrice, getMrp, formatInr } from '../../lib/pricing';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Minus, Plus, ShoppingCart, Truck, Package as PackageIcon, Leaf, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { sanitizeHtml, isHtmlEmpty } from '../../lib/html';
import '../../components/rich-text-editor.css';

export default function ProductDetail() {
    const { slugOrId } = useParams();
    const nav = useNavigate();
    const { customer } = useCustomerAuth();
    const { addItem } = useCart();
    const [product, setProduct] = useState(null);
    const [category, setCategory] = useState(null);
    const [brand, setBrand] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qty, setQty] = useState(1);
    const [activeImg, setActiveImg] = useState(0);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                // Try to fetch by id first (works if UUID given), else search by slug/sku via list endpoint
                let p = null;
                try {
                    const r = await api.get(`/products/${slugOrId}`);
                    p = r.data;
                } catch {
                    try {
                        const r2 = await api.get(`/products/by-slug/${slugOrId}`);
                        p = r2.data;
                    } catch {
                        const list = await api.get('/products', { params: { q: slugOrId, limit: 5 } });
                        p = (list.data.items || []).find(x => x.slug === slugOrId || x.sku === slugOrId) || (list.data.items || [])[0];
                    }
                }
                if (!p) { setProduct(null); return; }
                setProduct(p);
                if (p.category_id) {
                    try { const cr = await api.get(`/categories/${p.category_id}`); setCategory(cr.data); } catch { }
                }
                if (p.brand_id) {
                    try { const br = await api.get('/brands'); setBrand((br.data || []).find(x => x.id === p.brand_id)); } catch { }
                }
            } finally { setLoading(false); }
        })();
    }, [slugOrId]);

    if (loading) return <div className="bg-white min-h-screen"><CustomerHeader /><div className="max-w-[1400px] mx-auto px-4 py-16"><LoadingBlock /></div><CustomerFooter /></div>;
    if (!product) return <div className="bg-white min-h-screen"><CustomerHeader /><div className="max-w-[1400px] mx-auto px-4 py-16"><EmptyState icon={PackageIcon} title="Product not found" /></div><CustomerFooter /></div>;

    const resolved = resolveProductUnitPrice(product, customer?.type, customer?.level);
    const price = resolved.price;
    const unitCode = resolved.unit_code;
    const mrp = resolved.mrp || getMrp(product, customer?.type);
    const discount = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
    const available = product.inventory?.available || 0;
    const specFields = category?.spec_fields || [];

    const handleAdd = () => {
        addItem(product, qty, price, unitCode);
        toast.success(`${qty} × ${product.name} added to cart`);
    };

    const handleBuyNow = () => {
        addItem(product, qty, price, unitCode);
        nav('/checkout');
    };

    return (
        <div className="bg-white min-h-screen">
            <CustomerHeader />
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
                {/* Breadcrumb */}
                <div className="text-xs text-neutral-500 mb-4">
                    <span>Home</span> / {category && <><a href={`/category/${category.slug}`}>{category.name}</a> / </>}<span className="text-neutral-900">{product.name}</span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Images */}
                    <div>
                        <div className="aspect-square rounded-xl bg-neutral-100 overflow-hidden" data-testid="pdp-main-image">
                            <SafeImage
                                src={product.images?.[activeImg]}
                                alt={product.name}
                                className="h-full w-full object-cover"
                                iconClassName="h-12 w-12"
                            />
                        </div>
                        {product.images?.length > 1 && (
                            <div className="mt-3 grid grid-cols-5 gap-2">
                                {product.images.map((img, i) => (
                                    <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square rounded-md overflow-hidden border-2 ${i === activeImg ? 'border-green-500' : 'border-transparent'}`}>
                                        <SafeImage src={img} alt="" className="h-full w-full object-cover" iconClassName="h-5 w-5" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* Details */}
                    <div>
                        {brand && <div className="text-xs uppercase font-semibold text-neutral-500" data-testid="pdp-brand">{brand.name}</div>}
                        <h1 className="text-2xl sm:text-3xl font-bold mt-1 tracking-tight" data-testid="pdp-name">{product.name}</h1>
                        <div className="text-sm text-neutral-500 mt-1 font-mono">SKU: {product.sku}</div>
                        {product.short_description && <p className="mt-3 text-neutral-700">{product.short_description}</p>}

                        <div className="mt-5 flex items-baseline gap-3 flex-wrap">
                            <span className="text-3xl font-bold" data-testid="pdp-price">{formatInr(price)}</span>
                            <span className="text-sm text-neutral-500">/ {unitCode}</span>
                            {mrp > price && (
                                <>
                                    <span className="text-lg text-neutral-500 line-through">{formatInr(mrp)}</span>
                                    <Badge style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>{discount}% OFF</Badge>
                                </>
                            )}
                        </div>
                        {customer?.type === 'wholesale' && (
                            <div className="mt-2 text-xs font-medium" style={{ color: 'hsl(142 71% 30%)' }} data-testid="pdp-wholesale-badge">
                                Wholesale Level {customer.level} pricing applied
                            </div>
                        )}

                        <div className="mt-5 flex items-center gap-3">
                            {available > 0 ? (
                                <span className="text-sm text-green-700 font-medium">✓ In stock ({available} available)</span>
                            ) : (
                                <span className="text-sm text-red-600 font-medium">Out of stock</span>
                            )}
                        </div>

                        {/* Qty + Actions */}
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                            <div className="inline-flex items-center border rounded-md">
                                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2" data-testid="pdp-qty-dec"><Minus className="h-4 w-4" /></button>
                                <span className="px-4 tabular-nums w-12 text-center" data-testid="pdp-qty">{qty}</span>
                                <button onClick={() => setQty(Math.min(available || 99, qty + 1))} className="px-3 py-2" data-testid="pdp-qty-inc"><Plus className="h-4 w-4" /></button>
                            </div>
                            <Button onClick={handleAdd} disabled={available <= 0} data-testid="pdp-add-to-cart" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                                <ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart
                            </Button>
                            <Button variant="outline" onClick={handleBuyNow} disabled={available <= 0} data-testid="pdp-buy-now">Buy Now</Button>
                        </div>

                        {/* Trust */}
                        <div className="mt-8 grid grid-cols-3 gap-3 text-xs">
                            <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-blue-700" /><span>Nationwide delivery</span></div>
                            <div className="flex items-center gap-2"><Leaf className="h-4 w-4 text-green-700" /><span>Healthy plant guarantee</span></div>
                            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-blue-700" /><span>Secure payments</span></div>
                        </div>

                        {/* Specifications */}
                        {product.specs && Object.keys(product.specs).length > 0 && (
                            <Card className="mt-6 p-4">
                                <div className="text-sm font-semibold mb-2">Specifications</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                    {Object.entries(product.specs).map(([k, v]) => {
                                        const field = specFields.find(f => f.key === k);
                                        return (
                                            <div key={k} className="flex justify-between border-b py-1">
                                                <span className="text-neutral-600">{field?.label || k}</span>
                                                <span className="font-medium">{v}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Card>
                        )}

                        {product.description && !isHtmlEmpty(product.description) && (
                            <div className="mt-6">
                                <div className="text-sm font-semibold mb-2">About this product</div>
                                <div
                                    className="rte-content text-sm text-neutral-700"
                                    data-testid="product-description-rendered"
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description) }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <CustomerFooter />
        </div>
    );
}
